/* === Fifth Collective Buy Now bag guard ======================================
   Buy Now sends one unit of a piece to checkout on its own (a cart permalink).
   Shopify can swap the shopper's bag for that single-piece cart while doing so,
   which would leave the bag holding only the Buy Now unit after checkout.

   remember(variantId), called by Buy Now just before leaving for checkout, keeps
   a copy of the bag. On the shopper's next page (or a page restored from the
   back/forward cache) the bag is compared with that copy:
   - unchanged                                -> nothing to do
   - Shopify now has a different cart (the     -> the bag is rebuilt as it was, in
     Buy Now cart, or a new empty one after a     a fresh cart, so the Buy Now
     completed Buy Now order), or the same cart   checkout itself is never edited
     now holding only the Buy Now piece
   - changed in any other way (the shopper's   -> left alone
     own edits)
   The count and drawer are refreshed without opening the drawer; the cart page
   reloads. While the check runs, the count and cart page stay hidden (see
   snippets/fifth-collective-buy-now-guard.liquid) so no wrong number shows. */
(() => {
  if (window.__fifthCollectiveBuyNowGuardLoaded) return;
  window.__fifthCollectiveBuyNowGuardLoaded = true;

  const KEY = 'fc:buy-now-bag';
  const MAX_AGE = 3 * 24 * 60 * 60 * 1000;
  const MAX_ATTEMPTS = 3;
  const html = document.documentElement;
  const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';

  const read = () => {
    try {
      const saved = JSON.parse(localStorage.getItem(KEY) || 'null');
      return saved && saved.v === 1 && Array.isArray(saved.lines) ? saved : null;
    } catch (error) {
      return null;
    }
  };
  const write = (saved) => {
    try {
      localStorage.setItem(KEY, JSON.stringify(saved));
      return true;
    } catch (error) {
      return false;
    }
  };
  const forget = () => {
    try {
      localStorage.removeItem(KEY);
    } catch (error) {
      /* storage unavailable: nothing was kept */
    }
  };
  const settle = () => html.classList.remove('fc-bag-restoring');

  const withTimeout = (promise, ms) => Promise.race([
    promise,
    new Promise((resolve, reject) => window.setTimeout(() => reject(new Error('timeout')), ms))
  ]);

  const getCart = async () => {
    const response = await fetch(`${root}cart.js`, {
      credentials: 'same-origin',
      cache: 'no-store',
      headers: { Accept: 'application/json' }
    });
    if (!response.ok) throw new Error(`cart.js ${response.status}`);
    return response.json();
  };

  const post = async (path, body) => {
    const response = await fetch(`${root}${path}`, {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(body)
    });
    const data = await response.json().catch(() => ({}));
    if (!response.ok) {
      const error = new Error(data.description || data.message || `${path} ${response.status}`);
      error.status = response.status;
      throw error;
    }
    return data;
  };

  /* A cart line as /cart/add.js accepts it back. */
  const lineOf = (item) => {
    const line = { id: Number(item.variant_id || item.id), quantity: Number(item.quantity) };
    const properties = {};
    Object.entries(item.properties || {}).forEach(([name, value]) => {
      if (value !== null && value !== undefined && value !== '') properties[name] = value;
    });
    if (Object.keys(properties).length) line.properties = properties;
    const plan = item.selling_plan_allocation && item.selling_plan_allocation.selling_plan;
    if (plan && plan.id) line.selling_plan = plan.id;
    return line;
  };
  /* Cart tokens can carry a "?key=" suffix; the cart itself is the part before it. */
  const cartId = (token) => String(token || '').split('?')[0];
  const signature = (lines) => lines
    .map((line) => `${line.id}:${line.quantity}:${line.selling_plan || ''}:${JSON.stringify(line.properties || {})}`)
    .sort()
    .join('|');

  /* One tab at a time, so two tabs opened together cannot rebuild the bag twice. */
  const withLock = (task) => {
    if (navigator.locks && typeof navigator.locks.request === 'function') {
      return navigator.locks.request(KEY, task);
    }
    return task();
  };

  /* Drop Shopify's cart cookie so the next add starts a new cart: the bag is
     rebuilt there and the Buy Now checkout keeps its own cart untouched.
     Returns false when the cookie cannot be removed from script. */
  const startFreshCart = () => {
    const hasCart = () => /(?:^|;\s*)cart=/.test(document.cookie);
    if (!hasCart()) return false;
    const host = window.location.hostname;
    const parts = host.split('.');
    const domains = ['', host, `.${host}`];
    if (parts.length > 2) domains.push(`.${parts.slice(-2).join('.')}`);
    domains.forEach((domain) => {
      document.cookie = `cart=; Max-Age=0; path=/${domain ? `; domain=${domain}` : ''}`;
    });
    return !hasCart();
  };

  const addLines = async (lines) => {
    if (!lines.length) return;
    try {
      await post('cart/add.js', { items: lines });
    } catch (error) {
      /* One piece can no longer be added (e.g. it sold out): add the rest. */
      for (const line of lines) {
        try {
          await post('cart/add.js', { items: [line] });
        } catch (lineError) {
          console.warn('Fifth Collective: could not return a piece to the bag', line.id, lineError);
        }
      }
    }
  };

  const rebuild = async (saved) => {
    if (!startFreshCart()) await post('cart/clear.js', {});
    await addLines(saved.lines);
    const details = {};
    if (saved.note) details.note = saved.note;
    if (saved.attributes && Object.keys(saved.attributes).length) details.attributes = saved.attributes;
    if (Object.keys(details).length) await post('cart/update.js', details).catch(() => {});
    if (saved.discounts && saved.discounts.length) {
      await post('cart/update.js', { discount: saved.discounts.join(',') }).catch(() => {});
    }
  };

  /* Show the bag as it now is: the drawer and count in place, the cart page by reloading. */
  const refreshBag = async () => {
    if (document.getElementById('main-cart-items')) {
      window.location.reload();
      return true;
    }
    const drawer = document.querySelector('cart-drawer');
    const ids = drawer && typeof drawer.getSectionsToRender === 'function'
      ? drawer.getSectionsToRender().map((section) => section.section || section.id).filter(Boolean)
      : ['cart-icon-bubble'];
    const response = await fetch(`${root}?sections=${ids.join(',')}`, {
      credentials: 'same-origin',
      cache: 'no-store',
      headers: { Accept: 'application/json' }
    });
    if (!response.ok) return false;
    const sections = await response.json();
    if (drawer && typeof drawer.renderContents === 'function') {
      drawer.renderContents({ sections }, true);
      return false;
    }
    const bubble = document.getElementById('cart-icon-bubble');
    const next = sections['cart-icon-bubble'] && new DOMParser().parseFromString(sections['cart-icon-bubble'], 'text/html').querySelector('.shopify-section');
    if (bubble && next) bubble.innerHTML = next.innerHTML;
    return false;
  };

  /* What to do with the bag the shopper came back to. */
  const decide = (saved, cart) => {
    const current = (cart.items || []).map(lineOf);
    if (signature(current) === signature(saved.lines)) return 'unchanged';
    if (cartId(cart.token) !== cartId(saved.token)) return 'rebuild';
    const onlyBuyNowPiece = current.length > 0 && current.every((line) => line.id === saved.buyNow && !line.properties);
    return onlyBuyNowPiece ? 'rebuild' : 'changed';
  };

  let running = null;

  const run = async () => {
    const saved = read();
    if (!saved) {
      settle();
      return;
    }
    if (Date.now() - saved.at > MAX_AGE) {
      forget();
      settle();
      return;
    }
    html.classList.add('fc-bag-restoring');
    let reloading = false;
    try {
      await withLock(async () => {
        const latest = read();
        /* Another tab already put the bag back: just show it here. */
        if (!latest || latest.at !== saved.at) {
          reloading = await refreshBag();
          return;
        }
        const cart = await getCart();
        const action = decide(latest, cart);
        if (action === 'rebuild') {
          await rebuild(latest);
          forget();
          reloading = await refreshBag();
          return;
        }
        forget();
      });
    } catch (error) {
      console.warn('Fifth Collective: could not check the bag after Buy Now', error);
      const attempts = (saved.attempts || 0) + 1;
      if (attempts >= MAX_ATTEMPTS) forget();
      else write({ ...saved, attempts });
    } finally {
      if (!reloading) settle();
    }
  };

  const check = () => {
    if (!running) running = run().finally(() => { running = null; });
    return running;
  };

  /* Called by Buy Now before it leaves for checkout. */
  const remember = async (variantId) => {
    /* A bag still being put back from an earlier Buy Now is finished first,
       so the copy taken now is the real bag. */
    if (running) await running.catch(() => {});
    const cart = await withTimeout(getCart(), 4000);
    const saved = {
      v: 1,
      at: Date.now(),
      token: cart.token,
      buyNow: Number(variantId),
      lines: (cart.items || []).map(lineOf),
      note: cart.note || '',
      attributes: cart.attributes || {},
      discounts: (cart.discount_codes || []).filter((code) => code && code.code && code.applicable !== false).map((code) => code.code)
    };
    if (!write(saved)) throw new Error('storage unavailable');
    /* Keep the count hidden from here: the page may come back from the back/forward cache.
       If the shopper never actually leaves, it shows again shortly. */
    html.classList.add('fc-bag-restoring');
    window.setTimeout(() => { if (!running) settle(); }, 8000);
  };

  window.FifthCollectiveBuyNow = Object.freeze({ remember, check });

  check();
  window.addEventListener('pageshow', (event) => {
    if (event.persisted) check();
  });
  /* A tab left open in the background catches up when it is shown again. */
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible' && read()) check();
  });
})();
